import {
	AfterViewInit,
	Component,
	ElementRef,
	Inject,
	Input,
	OnChanges,
	OnDestroy,
	PLATFORM_ID,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import { coerceBoolean } from '@app-shared/decorators/coerce-boolean.decorator';
import { TranslateService } from '@ngx-translate/core';
import { Chart, registerables, LayoutPosition } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { isPlatformBrowser } from '@angular/common';

Chart.register(...registerables);
Chart.register(ChartDataLabels);
@Component({
	selector: 'app-doughnut-pie-chart',
	templateUrl: './doughnut-pie-chart.component.html',
	styleUrls: ['./doughnut-pie-chart.component.scss']
})
export class DoughnutPieChartComponent implements OnChanges, AfterViewInit, OnDestroy {
	private static idCounter = 0;

	@ViewChild('chartCanvas') canvasRef?: ElementRef<HTMLCanvasElement>;
	@ViewChild('wrapper') wrapperRef?: ElementRef<HTMLElement>;

	private chart: Chart | null = null;
	private viewInitialized = false;
	private readonly generatedId = `doughnut-chart-${++DoughnutPieChartComponent.idCounter}`;
	private resizeObserver?: ResizeObserver;
	private readonly isBrowser: boolean;

	constructor(public translate: TranslateService, @Inject(PLATFORM_ID) platformId: object) {
		this.isBrowser = isPlatformBrowser(platformId);
	}

	@Input() idChart?: string;
	@Input() labels: number[] | string[] = [];
	@Input() data: number[] = [];
	@Input() typeChart: any = 'doughnut';
	@Input() positionChart: LayoutPosition = 'bottom';
	@Input() colors: string[] | null = null;

	@coerceBoolean() @Input() onlyValueTooltip: boolean = false;
	@coerceBoolean() @Input() onlyPercentageTooltip: boolean = false;
	@coerceBoolean() @Input() displayDatalabels: boolean = false;

	get canvasId(): string {
		return this.idChart || this.generatedId;
	}

	ngAfterViewInit(): void {
		if (!this.isBrowser) {
			return;
		}
		this.viewInitialized = true;
		this.setupResizeObserver();
		this.handleInitChartjs();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!this.isBrowser) {
			return;
		}
		if (!this.viewInitialized) {
			return;
		}
		if (changes['data'] || changes['labels'] || changes['colors']) {
			this.handleInitChartjs();
		}
	}

	ngOnDestroy(): void {
		this.destroyChart();
		this.resizeObserver?.disconnect();
	}

	private handleInitChartjs(): void {
		if (!this.isBrowser) {
			return;
		}
		if (!Array.isArray(this.data) || !Array.isArray(this.labels) || !this.data.length) {
			this.destroyChart();
			return;
		}

		let context: CanvasRenderingContext2D | null = null;
		try {
			const canvas = this.canvasRef?.nativeElement;
			context = canvas?.getContext('2d') ?? null;
		} catch (error) {
			console.warn('Skipping doughnut chart render (SSR/DOM not supported).', error);
			return;
		}
		if (!context) {
			console.error('Failed to acquire 2D context for doughnut chart');
			return;
		}

		this.destroyChart();

		// Ensure the canvas has a non-zero size at first render
		this.ensureCanvasSize();

		const customColors = [
			'#054659',
			'#F18700',
			'#2A77AD',
			'#F9B000',
			'#3BB93B',
			'#7460DD',
			'#0099C9',
			'#F0BE4B',
			'#61CE61',
			'#20829E',
			'#9487D3',
			'#FFAC41',
			'#50BEDF',
			'#B9DFB9',
			'#6952D9',
			'#0DA9D9',
			'#F29E2F',
			'#1CAE94',
			'#B47F03',
			'#3494D9',
			'#503FA6',
			'#73D973',
			'#D97B00',
			'#EBA70A',
			'#EBC70A'
		];

		const palette = this.buildPalette(customColors);
		const total = this.data.reduce((accumulator, current) => accumulator + current, 0) || 1;
		const percentages = this.data.map(value => (value / total) * 100);

		try {
			this.chart = new Chart(context, {
				type: this.typeChart,
				data: {
					labels: [...this.labels],
					datasets: [
						{
							label: '',
							data: [...this.data],
							backgroundColor: palette,
							borderColor: palette,
							borderWidth: 0
						}
					]
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					cutout: '50%',
					plugins: {
						tooltip: {
							callbacks: {
								title: tooltipItems => tooltipItems[0]?.label || '',
								label: context => {
									const index = context.dataIndex ?? 0;
									const label = context.label || '';
									const value =
										(Array.isArray(context.dataset.data)
											? Number(context.dataset.data[index])
											: Number(context.parsed)) || 0;
									const percentage = percentages[index] || 0;
									const clicksLabel = this.translate.instant('clicks');
									const percentageLabel = `${percentage.toFixed(1)}%`;
									const valueLabel = `${value} ${clicksLabel}`;

									return this.onlyPercentageTooltip
										? percentageLabel
										: this.onlyValueTooltip
										? valueLabel
										: `${percentageLabel} | ${valueLabel}`;
								}
							},
							bodyFont: {
								size: 12,
								weight: 'bold'
							},
							bodyColor: '#FFFFFF',
							titleColor: '#FFFFFF',
							backgroundColor: '#262626',
							position: 'nearest',
							displayColors: true
						},
						legend: {
							display: false,
							position: this.positionChart,
							labels: {
								padding: 20,
								font: {
									size: 14,
									weight: 400,
									lineHeight: 22,
									family: 'ZT Nature'
								},
								usePointStyle: true,
								boxHeight: 7,
								pointStyleWidth: 10
							}
						},
						datalabels: {
							display: this.displayDatalabels,
							color: '#ffffff',
							font: {
								weight: '500',
								size: 16,
								lineHeight: 22
							}
						}
					}
				}
			});
		} catch (error) {
			console.warn('Skipping doughnut chart render (SSR/DOM not supported).', error);
			this.destroyChart();
		}
	}

	private setupResizeObserver(): void {
		if (typeof ResizeObserver === 'undefined') {
			return;
		}
		const wrapper = this.wrapperRef?.nativeElement;
		if (!wrapper) return;

		this.resizeObserver = new ResizeObserver(() => {
			if (this.chart) {
				this.chart.resize();
			} else if (this.viewInitialized) {
				// Try initializing again if size becomes available after async layout
				this.handleInitChartjs();
			}
		});
		this.resizeObserver.observe(wrapper);
	}

	private ensureCanvasSize(): void {
		const wrapper = this.wrapperRef?.nativeElement;
		const canvas = this.canvasRef?.nativeElement;
		if (!wrapper || !canvas) {
			return;
		}
		const rect = wrapper.getBoundingClientRect();
		const fallback = 220;
		const width = rect.width > 0 ? Math.floor(rect.width) : fallback;
		const heightCandidate = rect.height > 0 ? Math.floor(rect.height) : width;
		const height = heightCandidate > 0 ? heightCandidate : fallback;
		// Set initial size - Chart.js will manage responsive behavior after init
		canvas.width = width;
		canvas.height = height;
	}

	private buildPalette(customColors: string[]): string[] {
		const paletteSource = this.colors?.length ? this.colors : customColors;
		const palette = [...paletteSource];
		while (palette.length < this.data.length) {
			palette.push(...customColors);
			if (palette.length >= this.data.length) {
				break;
			}
		}
		return palette.slice(0, this.data.length);
	}

	private destroyChart(): void {
		if (this.chart) {
			this.chart.destroy();
			this.chart = null;
		}
	}
}
